import AutoComplete from './autocomplete';
import dom from '../util/dom';
import cookie from '../util/cookie';
import addressit from 'addressit';
import distance from '../util/distance';

// mapbox api constants
const baseurl = 'https://api.mapbox.com/geocoding/v5/mapbox.places-permanent';
const key = [
  'pk',
  'eyJ1Ijoia2V2aW5jZW5uaXMiLCJhIjoiY2l5Y2RoMXQ0MDB0OTJxcnNnMWZkNXhmZCJ9',
  'ZGYfhuRuyKS_uEPFkdX87g'
].join('.');

const placeholders = [
  { lat: 42.3600, lng: -71.0588, placeholder: '123 Main St, Boston MA' },
  { lat: 40.7127, lng: -74.0059, placeholder: '123 Main St, New York NY' },
  { lat: 34.0522, lng: -118.2436, placeholder: '123 Main St, Los Angeles CA' },
  { lat: 39.7392, lng: -104.9902, placeholder: '123 Main St, Denver CO' },
  { lat: 38.8920, lng: -77.0199, placeholder: '123 Main St, Washington DC' },
  { lat: 39.9612, lng: -82.9988, placeholder: '123 Main St, Columbus OH' },
  { lat: 37.7749, lng: -122.4194, placeholder: '123 Main St, San Francisco CA' }
];

export default class AddressAutoComplete extends AutoComplete {

  constructor( opts ) {
    super( opts );

    this.geoip = { latitude: 42.3601, longitude: -71.0589 };

    try {
      const base64 = cookie.getItem('geoip');
      const json   = window.atob( base64 );
      const geo    = JSON.parse( json );

      Object.assign( this.geoip, geo );
    } catch ( err ) {}

    this.location = { lat: this.geoip.latitude, lng: this.geoip.longitude };

    const sorted = placeholders.slice().sort( ( a, b ) => {
      const da = distance( a, this.location );
      const db = distance( b, this.location );
      return da - db;
    });

    const placeholder = sorted[ 0 ].placeholder;

    this.input.placeholder = placeholder;
  }

  // run a search
  search( str ) {
    const query = encodeURIComponent( str );

    let url = `${ baseurl }/${ query }.json`;

    url += `?access_token=${ key }`;
    url += '&types=address';
    url += '&country=US';

    if ( this.geoip.latitude && this.geoip.longitude ) {
      url += `&proximity=${ this.geoip.longitude },${ this.geoip.latitude }`;
    }

    return new Promise( ( resolve, reject ) => {
      dom.ajax( { method: 'GET', url }, ( err, data ) => {
        if ( err ) {
          return reject( err );
        }
        const features      = this.extractFeatures( data );
        const suggestions   = this.filterFeatures( features );
        const neighborhoods = this.showNeighborhoods( suggestions );

        for ( let suggestion of suggestions ) {
          suggestion.locality = this.displayCity(
            suggestion,
            str,
            Boolean( neighborhoods )
          );
        }

        return resolve( suggestions );
      });
    });
  }

  renderSuggestion( place ) {
    let html = '';

    html += `<strong>${ place.name }</strong>`;
    html += `${ place.locality }, ${ place.state } ${ place.postal_code }`;

    return html;
  }

  // format mapbox results
  extractFeatures( result ) {
    return result.features.map( feature => {
      // prop names on these objects are basically like
      // "region.ab7c38d8903", so we need to look for
      // prefixes like "region." to actually find things
      function find( str ) {
        return feature.context.reduce( ( p, c ) => {
          return c.id.indexOf( str ) === 0 ? c : p;
        }, {} );
      }

      return {
        name: feature.address + ' ' + feature.text,
        street_number: feature.address,
        street: feature.text,
        city: find('place.').text,
        state: ( find('region.').short_code || '' ).replace( 'US-', '' ),
        postal_code: find('postcode.').text || '',
        neighborhood: find('neighborhood.').text || '',
        borough: find('locality.').text || '',
        longitude: feature.geometry.coordinates[ 0 ],
        latitude: feature.geometry.coordinates[ 1 ]
      };
    });
  }

  // filter mapbox results and grab the top 3
  filterFeatures( features ) {
    return features.filter( place => {
      return !!place.postal_code && !!place.street_number;
    }).slice( 0, 3 );
  }

  // should we show neighborhoods?
  // return true when multiple results share the same street_number,
  // street, and city
  showNeighborhoods( places ) {
    const names = [];

    return places.some( place => {
      const city  = place.borough || place.city;
      const name  = `${ place.name } ${ city }, ${ place.state }`;
      const isDup = names.indexOf( name ) !== -1;

      names.push( name );

      return isDup;
    });
  }

  // get the preferred locality for a place
  displayCity( place, query, forceNeighborhood ) {
    if ( forceNeighborhood ) {
      if ( place.neighborhood ) {
        return `${ place.neighborhood }, ${ place.borough || place.city }`;
      }
      return place.borough || place.city;
    }

    const parsed = addressit( query );

    // if we can't find a city name in the query, just show the
    // city or borough
    if ( !parsed || !parsed.regions.length ) {
      return place.borough || place.city;
    }

    // look for neighborhood, city, or borough matches in the search term
    // and show the user what they typed
    const q = parsed.regions[ 0 ].toLowerCase();

    const { neighborhood, borough, city } = place;

    if ( borough && borough.toLowerCase().indexOf( q ) === 0 ) {
      return borough;
    }

    if ( city && city.toLowerCase().indexOf( q ) === 0 ) {
      return city;
    }

    if ( neighborhood && neighborhood.toLowerCase().indexOf( q ) === 0 ) {
      return `${ neighborhood }, ${ borough || city }`;
    }

    return borough || city;
  }

}
